<template>
  <div>
    <v-dialog v-model="show" width="800" @click:outside="closeDialog">
      <v-card>
        <v-card-title class="third">
          <span class="d-flex justify-center flex-grow-1 font-weight-bold text-h5 white--text">Gestion des ordonnances</span>
          <v-icon size="28" color="white" @click="closeDialog">mdi-close</v-icon>
        </v-card-title>
        <v-card-text class="mt-4 d-flex flex-column">
          <div class="d-flex flex-column">
            <span class="text-body-1 font-weight-bold"> Soin du {{ documentationItem.session.date }} - {{ documentationItem.session.careType }} - Cheval : {{ documentationItem.horse.name }}</span>
            <v-btn outlined class="col-4 my-6 py-1 third--text no-caps-btn" color="white" @click="openFileUploadModal">
              <v-icon>mdi-plus</v-icon>
              Ajouter une ordonnance
            </v-btn> 
          </div>
          <div class="overflow-y-auto max-height-300">
            <v-data-table
              class="table-container-borders"
              :headers="headers"
              :items="items"
              :loading="loading ? 'third' : false"
              :sort-by="['date']"
              :hide-default-footer="true"
              :items-per-page="-1"
              loader-height="6"
            >
            <template #item.action="{ item }">
              <v-btn color="black" icon @click="deleteDocument(item)">
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
              <v-btn color="black" icon @click="openDocument(item)">
                <v-icon>mdi-eye-outline</v-icon>
              </v-btn>
            </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <FileUpload v-if="fileUploadModal" :show="fileUploadModal" :uploading="uploadLoading" title="Ajouter une ordonnance" :document-types-allowed="['application/pdf', 'image/jpeg', 'image/png']" @closeModal="closeFileUploadModal" @fileSelected="onFileSelected" />
    <FilePreview v-if="filePreviewModal" :show="filePreviewModal" :document="previewDocument" @closeModal="closeFilePreviewModal" />
    <ConfirmationModal
      title="Supprimer le document"
      :activator.sync="deleteConfirmationModal"
      :close-modal="closeDeleteConfirmationModal"
      :action="confirmDeleteDocument"
      :loading="deleteLoading"
      z-index="500"
      btn-label="Supprimer"
      btn-icon="mdi-delete"
    >
      <template #body>
        <h3 class="black--text">Êtes-vous sûr de vouloir supprimer ce document ?</h3>
      </template>
    </ConfirmationModal>
  </div>
</template>

<script>
import { ref, onMounted } from '@vue/composition-api';
import FileUpload from '@/components/common/FileUpload.vue';
import FilePreview from '@/components/common/FilePreview.vue';
import ConfirmationModal from '@/components/common/ConfirmationModal.vue';
import { createHorseCareUploadUrl, uploadHorseCareToS3, getHorseCareListing, deleteHorseCareDocument, getHorseCareDocumentUrl } from '@/helpers/request';
import { sendNotification } from '@/helpers/utils';

export default {
  components: { FileUpload, FilePreview, ConfirmationModal },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    documentationItem: {
      type: Object,
      default: () => {}
    }
  },
  setup (props, { emit }) {
    const headers = ref([
      { text: 'Date d\'ajout', value: 'date', align: 'center', sortable: true, width: '25%' },
      { text: 'Nom', value: 'name', align: 'center', sortable: true, width: '50%' },
      { text: 'Action', value: 'action', align: 'center', sortable: false, width: '25%' }
    ]);

    const items = ref([]);

    const loading = ref(false);

    const fileUploadModal = ref(false);
    const uploadLoading = ref(false);
    
    const filePreviewModal = ref(false);
    const previewDocument = ref(null);

    const deleteConfirmationModal = ref(false);
    const documentToDelete = ref(null);
    const deleteLoading = ref(false);

    const closeDialog = () => {
      emit('closeModal');
    };

    const openFileUploadModal = () => {
      fileUploadModal.value = true;
    };

    const closeFileUploadModal = () => {
      fileUploadModal.value = false;
    };

    const onFileSelected = async (file) => {
      if (!file || !props.documentationItem?.horse?.uuid || !props.documentationItem?.session?.id) {
        sendNotification('error', 'Informations manquantes pour l\'upload');
        return;
      }

      uploadLoading.value = true;
      const horseUuid = props.documentationItem.horse.uuid;
      const sessionId = props.documentationItem.session.id;
      const response = await createHorseCareUploadUrl(horseUuid, sessionId, file.name);
      if (!response || !response.success || !response.data) {
        sendNotification('error', response.message);
        return
      }

      const uploadResponse = await uploadHorseCareToS3(response.data, file);

      sendNotification(uploadResponse?.success ? 'success' : 'error', uploadResponse.message);
      uploadLoading.value = false;
      closeFileUploadModal();

      if (uploadResponse?.success) {
        await fetchItems();
      }
      
    };

    const openDocument = async (item) => {
      loading.value = true;
      const response = await getHorseCareDocumentUrl(props.documentationItem.horse.uuid, props.documentationItem.session.id, item.name);
      loading.value = false;

      if (!response || !response.success || !response.data) {
        sendNotification('error', response?.message || 'Erreur lors de la récupération de l\'URL du document');
        return;
      }

      previewDocument.value = { url: response.data, fileName: item.name };
      filePreviewModal.value = true;
    };

    const closeFilePreviewModal = () => {
      filePreviewModal.value = false;
      previewDocument.value = null;
    };

    const deleteDocument = (item) => {
      documentToDelete.value = item;
      deleteConfirmationModal.value = true;
    };

    const closeDeleteConfirmationModal = () => {
      deleteConfirmationModal.value = false;
      documentToDelete.value = null;
    };

    const fetchItems = async () => {
      loading.value = true;
      const response = await getHorseCareListing(props.documentationItem.horse.uuid, props.documentationItem.session.id);
      if (!response?.success) {
        sendNotification('error', response.message);
      } else {
        items.value = response.data || [];
      }
      loading.value = false;
    };

    const confirmDeleteDocument = async () => {
      deleteLoading.value = true;
      const response = await deleteHorseCareDocument(props.documentationItem.horse.uuid, props.documentationItem.session.id, documentToDelete.value.name);

      sendNotification(response?.success ? 'success' : 'error', response.message);
      deleteLoading.value = false;
      closeDeleteConfirmationModal();

      if (response?.success) {
        await fetchItems();
      } 
    };

    onMounted(() => {
      fetchItems();
    });

    return {
      closeDialog,
      headers,
      items,
      loading,
      fileUploadModal,
      uploadLoading,
      openFileUploadModal,
      closeFileUploadModal,
      onFileSelected,
      openDocument,
      closeFilePreviewModal,
      filePreviewModal,
      previewDocument,
      deleteDocument,
      closeDeleteConfirmationModal,
      confirmDeleteDocument,
      deleteConfirmationModal,
      deleteLoading
    };
  }
};
</script>